import { useEffect, useMemo, useState } from'react'
import { useNavigate } from'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from'@/components/ui/Card'
import { Button } from'@/components/ui/Button'
import { Input } from'@/components/ui/Input'
import { AlertCircle, ArrowLeft, Search, Trash2 } from'lucide-react'

type LookupUser = {
 user_id: number
 username?: string
 email?: string
 approval_status?: string | null
 mfa_enabled?: boolean | null
 created_at?: string | null
 source?:'mongo' |'sql' | string
}

export function AdminUserPurge() {
 const navigate = useNavigate()
 const [checkingAdmin, setCheckingAdmin] = useState(true)
 const [isAdmin, setIsAdmin] = useState(false)

 const [email, setEmail] = useState('')
 const [userId, setUserId] = useState('')
 const [lookupUser, setLookupUser] = useState<LookupUser | null>(null)
 const [status, setStatus] = useState<{ type:'info' |'error' |'success'; text: string } | null>(null)
 const [busy, setBusy] = useState(false)

 const [directoryQuery, setDirectoryQuery] = useState('')
 const [directoryUsers, setDirectoryUsers] = useState<LookupUser[]>([])
 const [directoryLoading, setDirectoryLoading] = useState(false)

 const resolvedUserId = useMemo(() => {
 if (lookupUser?.user_id) return lookupUser.user_id
 const n = Number(userId)
 return Number.isFinite(n) && n > 0 ? n : null
 }, [lookupUser, userId])

 useEffect(() => {
 let cancelled = false
 async function check() {
 try {
 const res = await fetch('/api/admin/check', { credentials:'include' })
 const data = await res.json().catch(() => ({}))
 if (cancelled) return
 if (res.ok && data.success) {
 setIsAdmin(Boolean(data.is_admin))
 if (!data.is_admin) {
 setStatus({ type:'error', text:'Admin access required.' })
 }
 } else {
 setStatus({ type:'error', text:'Please log in again.' })
 }
 } catch {
 if (!cancelled) setStatus({ type:'error', text:'Failed to verify admin status.' })
 } finally {
 if (!cancelled) setCheckingAdmin(false)
 }
 }
 check()
 return () => {
 cancelled = true
 }
 }, [])

 const fetchDirectory = async (q: string) => {
 setDirectoryLoading(true)
 try {
 const res = await fetch(`/api/admin/user-directory?q=${encodeURIComponent(q)}&limit=200`, {
 credentials:'include',
 })
 const data = await res.json().catch(() => ({}))
 if (res.ok && data.success) {
 setDirectoryUsers(Array.isArray(data.users) ? data.users : [])
 } else {
 setDirectoryUsers([])
 }
 } catch {
 setDirectoryUsers([])
 } finally {
 setDirectoryLoading(false)
 }
 }

 useEffect(() => {
 if (!checkingAdmin && isAdmin) {
 fetchDirectory('')
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [checkingAdmin, isAdmin])

 const doLookup = async () => {
 const e = email.trim().toLowerCase()
 if (!e) {
 setStatus({ type:'error', text:'Enter an email to look up.' })
 return
 }
 setBusy(true)
 setStatus(null)
 setLookupUser(null)
 try {
 const res = await fetch(`/api/admin/users/lookup?email=${encodeURIComponent(e)}`, {
 credentials:'include',
 })
 const data = await res.json().catch(() => ({}))
 if (res.ok && data.success) {
 setLookupUser(data.user)
 setUserId(String(data.user?.user_id ??''))
 setStatus({ type:'success', text: `Found user_id=${data.user?.user_id} (${data.user?.source ||'unknown'})` })
 } else {
 setStatus({ type:'error', text: data.error ||'User not found' })
 }
 } catch {
 setStatus({ type:'error', text:'Lookup failed' })
 } finally {
 setBusy(false)
 }
 }

 const doFetchById = async () => {
 const id = Number(userId)
 if (!Number.isFinite(id) || id <= 0) {
 setStatus({ type:'error', text:'Enter a valid numeric user_id.' })
 return
 }

 setBusy(true)
 setStatus(null)
 setLookupUser(null)
 try {
 const res = await fetch(`/api/admin/users/${id}`, { credentials:'include' })
 const data = await res.json().catch(() => ({}))
 if (res.ok && data.success) {
 setLookupUser(data.user)
 setEmail(String(data.user?.email ??''))
 setStatus({ type:'success', text: `Loaded user_id=${data.user?.user_id} (${data.user?.source ||'unknown'})` })
 } else {
 setStatus({ type:'error', text: data.error ||'User not found' })
 }
 } catch {
 setStatus({ type:'error', text:'Fetch failed' })
 } finally {
 setBusy(false)
 }
 }

 const selectUser = (u: LookupUser) => {
 setLookupUser(u)
 setUserId(String(u.user_id ??''))
 setEmail(String(u.email ??''))
 setStatus({ type:'info', text: `Selected user_id=${u.user_id}` })
 }

 const doPurge = async () => {
 const id = resolvedUserId
 const e = email.trim().toLowerCase()

 if (!id && !e) {
 setStatus({ type:'error', text:'Provide either user_id or email.' })
 return
 }

 const ok = window.confirm(
 `Permanently delete this user and ALL their data. This cannot be undone.\n\nProceed?`
 )
 if (!ok) return

 setBusy(true)
 setStatus(null)
 try {
 let res: Response
 if (e && !id) {
 res = await fetch(`/api/admin/users/purge-by-email`, {
 method:'POST',
 credentials:'include',
 headers: {'Content-Type':'application/json' },
 body: JSON.stringify({ confirm: true, email: e }),
 })
 } else if (id) {
 res = await fetch(`/api/admin/users/${id}/purge`, {
 method:'POST',
 credentials:'include',
 headers: {'Content-Type':'application/json' },
 body: JSON.stringify({ confirm: true }),
 })
 } else {
 // unreachable, but keeps TS happy
 throw new Error('Missing target')
 }

 const data = await res.json().catch(() => ({}))
 if (res.ok && data.success) {
 setStatus({ type:'success', text: `User purged (user_id=${data.report?.user_id ?? id ??'?'})` })
 setLookupUser(null)
 } else {
 setStatus({ type:'error', text: data.error ||'Purge failed' })
 }
 } catch {
 setStatus({ type:'error', text:'Purge failed' })
 } finally {
 setBusy(false)
 }
 }

 return (
 <div className="max-w-4xl mx-auto px-4 py-8">
 <div className="flex items-center gap-3 mb-6">
 <Button variant="outline" onClick={() => navigate('/admin')} className="gap-2">
 <ArrowLeft className="w-4 h-4" />
 Back to Admin
 </Button>
 <div>
 <h1 className="text-2xl font-semibold">User Cleanup</h1>
 <p className="text-sm text-muted-foreground">Boot out a user by deleting all their stored data.</p>
 </div>
 </div>

    <Card className="border-bad/30">
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-bad" />
 Dangerous Operation
 </CardTitle>
 <CardDescription>
 This will permanently delete the user across MongoDB + SQLite. Use for dev testing and admin cleanup.
 </CardDescription>
 </CardHeader>
 <CardContent className="space-y-4">
 {checkingAdmin ? (
 <div className="text-sm text-muted-foreground">Checking admin access…</div>
 ) : !isAdmin ? (
 <div className="text-sm text-bad">{status?.text ||'Admin access required.'}</div>
 ) : (
 <>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
 <div className="md:col-span-2">
 <label className="block text-sm font-medium mb-1">Email</label>
 <Input
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 placeholder="user@example.com"
 autoComplete="off"
 onKeyDown={(e) => {
 if (e.key ==='Enter') doLookup()
 }}
 />
 </div>
 <div className="md:col-span-1">
 <label className="block text-sm font-medium mb-1">User ID</label>
 <Input
 value={userId}
 onChange={(e) => setUserId(e.target.value)}
 placeholder="123"
 inputMode="numeric"
 autoComplete="off"
 onKeyDown={(e) => {
 if (e.key ==='Enter') doFetchById()
 }}
 />
 </div>
 </div>

 <div className="flex flex-wrap gap-2">
 <Button variant="outline" onClick={doLookup} disabled={busy || !email.trim()}>
 <Search className="w-4 h-4 mr-2" />
 Lookup by email
 </Button>
 <Button variant="outline" onClick={doFetchById} disabled={busy || !userId.trim()}>
 <Search className="w-4 h-4 mr-2" />
 Fetch by ID
 </Button>
 <Button
 variant="destructive"
 onClick={doPurge}
 disabled={busy || (!resolvedUserId && !email.trim())}
 >
 <Trash2 className="w-4 h-4 mr-2" />
 Purge user
 </Button>
 </div>

 {lookupUser && (
 <div className="text-sm rounded-md border p-3 bg-muted/30">
 <div><span className="font-medium">Resolved:</span> user_id={lookupUser.user_id}</div>
 {lookupUser.username && <div><span className="font-medium">Username:</span> {lookupUser.username}</div>}
 {lookupUser.email && <div><span className="font-medium">Email:</span> {lookupUser.email}</div>}
 {lookupUser.approval_status !== undefined && (
 <div><span className="font-medium">Approval:</span> {String(lookupUser.approval_status)}</div>
 )}
 {lookupUser.mfa_enabled !== undefined && (
 <div><span className="font-medium">MFA enabled:</span> {String(lookupUser.mfa_enabled)}</div>
 )}
 {lookupUser.source && <div><span className="font-medium">Source:</span> {lookupUser.source}</div>}
 </div>
 )}

 <div className="pt-2 border-t">
 <div className="flex items-center justify-between gap-2 mb-2">
 <div>
 <div className="text-sm font-medium">All users</div>
 <div className="text-xs text-muted-foreground">Search by substring (email/username) and select a user to populate the form.</div>
 </div>
 <div className="flex items-center gap-2">
 <Input
 value={directoryQuery}
 onChange={(e) => setDirectoryQuery(e.target.value)}
 placeholder="Search users…"
 autoComplete="off"
 onKeyDown={(e) => {
 if (e.key ==='Enter') fetchDirectory(directoryQuery.trim())
 }}
 />
 <Button variant="outline" onClick={() => fetchDirectory(directoryQuery.trim())} disabled={directoryLoading}>
 <Search className={`w-4 h-4 ${directoryLoading ?'animate-pulse' :''}`} />
 </Button>
 </div>
 </div>

 <div className="overflow-x-auto rounded-md border">
 <table className="w-full text-sm">
 <thead className="bg-muted/40">
 <tr>
 <th className="text-left p-2">ID</th>
 <th className="text-left p-2">Username</th>
 <th className="text-left p-2">Email</th>
 <th className="text-left p-2">Approval</th>
 <th className="text-left p-2">MFA</th>
 <th className="text-right p-2">Action</th>
 </tr>
 </thead>
 <tbody>
 {directoryUsers.map((u) => (
 <tr key={u.user_id} className="border-t">
 <td className="p-2 font-mono">{u.user_id}</td>
 <td className="p-2">{u.username ||'-'}</td>
 <td className="p-2">{u.email ||'-'}</td>
 <td className="p-2">{u.approval_status ??'-'}</td>
 <td className="p-2">{u.mfa_enabled === undefined ?'-' : String(u.mfa_enabled)}</td>
 <td className="p-2 text-right">
 <Button variant="outline" onClick={() => selectUser(u)} className="h-8">
 Select
 </Button>
 </td>
 </tr>
 ))}
 {directoryUsers.length === 0 && (
 <tr>
 <td className="p-4 text-center text-muted-foreground" colSpan={6}>
 {directoryLoading ?'Loading…' :'No users found'}
 </td>
 </tr>
 )}
 </tbody>
 </table>
 </div>
 </div>

 {status && (
 <div
                className={`text-sm rounded-md border p-3 ${
                status.type ==='success'
                ?'border-ok/30 text-ok'
                : status.type ==='error'
                ?'border-bad/30 text-bad'
                :'border-soft text-secondary'
                }`}
 >
 {status.text}
 </div>
 )}
 </>
 )}
 </CardContent>
 </Card>
 </div>
 )
}

